import {
  Route,
  Navigate,
  Outlet,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { NotFoundPage } from "@pages/NotFoundPage";
import { AuthGuard } from "@components/auth/AuthGuard";
import { SessionTimeoutManager } from "@components/auth/SessionTimeoutManager";
import {
  Login,
  ForgotPassword,
  ResetPassword,
  ApplyNow,
  InviteCodeQuestion,
  EnterInviteCode,
  NeedToApplyFirst,
  VerifyInfoSetPassword,
} from "@features/auth";
import { FileTokenInitializer } from "@components/file-preview/FileTokenInitializer";
import { LanguageSelector } from "@components/layout/LanguageSelector";
import { GoogleTranslateRouteSync } from "@components/layout/GoogleTranslateRouteSync";
import { DashboardLayout } from "@components/layout/DashboardLayout";
import { DashboardRouteErrorElement } from "@components/layout/DashboardRouteErrorElement";
import { Dashboard } from "@features/dashboard/pages/Dashboard";
import { ApplicantDashboardLayout } from "@components/layout/ApplicantDashboardLayout";
import { ApplicationRouteGuard } from "@components/guards/ApplicationRouteGuard";
import { ApplicationDetailAccessGuard } from "@components/guards/ApplicationDetailAccessGuard";
import { FileHandlers } from "@components/file-preview";
import { ProtectedRoute } from "./ProtectedRoute";
import {
  LegacyDashboardPathRedirect,
  LegacyInviteEditRedirect,
  LegacyResidentDetailRedirect,
  LegacyReimbursementDetailRedirect,
  LegacyReimbursementsPathRedirect,
} from "./LegacyDashboardRedirects";
import { RootLauncherRedirect } from "./RootLauncherRedirect";
import {
  ApplicantDashboard,
  ResidentApplication,
  Messages,
  About,
  Property,
  Income,
  History,
  Signature,
  PaymentPage,
  Help,
  Profile,
  FairHousingActNotice,
  CreditReportingBureauNotice,
  AuthorizationForLibertyNotice,
} from "@features/applicant";
import { HelpAboutPage } from "@features/help-about";
import { UserProfilePage } from "@features/user-profile";
import { InviteeList } from "@features/invitee/pages/InviteeList";
import { InviteeAdd } from "@features/invitee/pages/InviteeAdd";
import { PolicyGuard } from "@components/guards/PolicyGuard";
import { ReimbursementsAccessGuard } from "@components/guards/ReimbursementsAccessGuard";
import { AdminOnlyGuard } from "@components/guards/AdminOnlyGuard";
import { AccessibleMenuGuard } from "@components/guards/AccessibleMenuGuard";
import { DashboardAccessGuard } from "@components/guards/DashboardAccessGuard";
import { ApplicationList } from "@features/application/pages/ApplicationList";
import { CompanyList, CompanyAdd, CompanyEdit } from "@features/company";
import { PropertyList, PropertyAdd, PropertyEdit } from "@features/property";
import { TransferProcessPage } from "@features/transfer";
import { YardiInviteListPage } from "@features/yardi-invite/pages/YardiInviteListPage";
import { YardiGuestSearchPage } from "@features/yardi-invite/pages/YardiGuestSearchPage";
import { renderReportChildRoutes } from "@features/reports/reportRoutes";
import { ImapInboxPage } from "@features/reports/pages/MailReportsPage";
import { MailLogDetailPage } from "@features/reports/pages/MailLogDetailPage";
import { ReportsSchedulerPage } from "@features/reports-scheduler";
import { UnderwritingConfigPage } from "@features/config";
import { ImpactNotesListPage } from "@features/impact-notes";
import { ReportDescriptionListPage } from "@features/report-description";
import { WhatsNewListPage } from "@features/whats-new";
import { QueuedJobsPage, FailedJobsPage, CronJobsPage } from "@features/jobs";
import { CronMonitorPage, CronMonitorJobsListPage } from "@features/cron-monitor";
import { NotificationSettingsListPage } from "@features/notification-settings";
import { PolicyListPage } from "@features/policy";
import { StockRegistryListPage } from "@features/stock-registry";
import {
  DocumentManagementPage,
  TermsConditionsEditPage,
  ServiceAgreementViewPage,
  ServiceAgreementEditPage,
  GdprConsentPage,
} from "@features/document-management";
import {
  OrganizationListPage,
  OrganizationEditPage,
} from "@features/organizations";
import {
  ReimbursementsListPage,
  ReimbursementsDetailPage,
  EorpDocumentPage,
} from "@features/reimbursements";
import { SystemAnnouncementListPage } from "@features/system-announcement";
import { DashboardTilesListPage } from "@features/dashboard-tiles";
import {
  EmailTemplateAddEditPage,
  EmailTemplatesListPage,
} from "@features/email-templates";
import { MailtrapListPage } from "@features/mailtrap";
import { RolesListPage } from "@features/roles";
import {
  PropertyMaintenancePage,
  TransferPage as PropertyMaintenanceTransferPage,
  ImportPage as PropertyMaintenanceImportPage,
} from "@features/property-maintenance";
import { ConfigLegacyPage } from "@features/config-legacy";
import {
  ScreeningInfoRequestsListPage,
  ReimbursementNotesListPage,
} from "@features/business-config";
import {
  UserManagementListPage,
  UserManagementAddPage,
  UserManagementEditPage,
  UserManagementAssignRolePage,
} from "@features/user-management";
import {
  ProductListPage,
  ProductAddPage,
  ProductEditPage,
} from "@features/product";
import { ModelListPage, ModelAddPage, ModelEditPage } from "@features/model";
import { ResidentListPage, ResidentDetailPage } from "@features/resident";
import { SalesTeamsPage } from "@features/sales-teams";
import { ApplyPage } from "@features/apply";

function RootLayout() {
  return (
    <AuthGuard>
      <FileTokenInitializer />
      <GoogleTranslateRouteSync />
      <SessionTimeoutManager />
      <Outlet />
      <LanguageSelector />
    </AuthGuard>
  );
}

function AdminShell() {
  return (
    <ProtectedRoute>
      <DashboardAccessGuard>
        <DashboardLayout />
      </DashboardAccessGuard>
    </ProtectedRoute>
  );
}

/**
 * Data router so hooks like `useBlocker` work (requires RouterProvider, not BrowserRouter).
 */
export const appRouter = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/file-handlers" element={<FileHandlers />} />
      <Route
        path="/explanation-service-aggrement/:id"
        element={
          <ProtectedRoute>
            <EorpDocumentPage />
          </ProtectedRoute>
        }
      />
      {/* GDPR consent deep link (exact) → dashboard + modal */}
      <Route
        path="/gdpr-consent"
        element={
          <ProtectedRoute>
            <GdprConsentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resident-application/terms_info/fair_housing_act"
        element={<FairHousingActNotice />}
      />
      <Route
        path="/resident-application/terms_info/credit_reporting_bureau/:applicantId?"
        element={<CreditReportingBureauNotice />}
      />
      <Route
        path="/resident-application/terms_info/authorization_for_liberty"
        element={<AuthorizationForLibertyNotice />}
      />
      <Route
        path="/applicant/application/terms_info/fair_housing_act"
        element={<FairHousingActNotice />}
      />
      <Route
        path="/applicant/application/terms_info/credit_reporting_bureau/:applicantId?"
        element={<CreditReportingBureauNotice />}
      />
      <Route
        path="/applicant/application/terms_info/authorization_for_liberty"
        element={<AuthorizationForLibertyNotice />}
      />
      <Route path="/apply/:inviteId" element={<ApplyPage />} />
      <Route
        path="/co-applicant/apply/:inviteId"
        element={<ApplyPage isCoApplicant />}
      />
      <Route
        path="/resident-application"
        element={
          <ProtectedRoute>
            <Navigate to="/applicant/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resident-application/:applicantId"
        element={
          <ProtectedRoute>
            <ApplicantDashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ResidentApplication />} />
      </Route>

      {/* Admin shell: layout has no URL segment (Angular LayoutComponent parity) */}
      <Route element={<AdminShell />} errorElement={<DashboardRouteErrorElement />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/help-about" element={<HelpAboutPage />} />
        <Route path="/user/profile" element={<UserProfilePage />} />
        <Route
          path="/invite"
          element={
            <PolicyGuard requiredPolicies={["invite_menu"]}>
              <InviteeList />
            </PolicyGuard>
          }
        />
        <Route
          path="/invite/add"
          element={
            <PolicyGuard requiredPolicies={["invite_menu"]}>
              <InviteeAdd />
            </PolicyGuard>
          }
        />
        <Route
          path="/invite/edit/:code"
          element={
            <PolicyGuard requiredPolicies={["invite_menu"]}>
              <InviteeAdd />
            </PolicyGuard>
          }
        />
        <Route
          path="/yardiinvite"
          element={
            <PolicyGuard requiredPolicies={["invite_menu"]}>
              <YardiInviteListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/yardiinvite/search"
          element={
            <PolicyGuard requiredPolicies={["invite_menu"]}>
              <YardiGuestSearchPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/company"
          element={
            <PolicyGuard requiredPolicies={["company_menu"]}>
              <CompanyList />
            </PolicyGuard>
          }
        />
        <Route
          path="/company/add"
          element={
            <PolicyGuard requiredPolicies={["company_menu"]}>
              <CompanyAdd />
            </PolicyGuard>
          }
        />
        <Route
          path="/company/edit/:id"
          element={
            <PolicyGuard requiredPolicies={["company_menu"]}>
              <CompanyEdit />
            </PolicyGuard>
          }
        />
        <Route
          path="/property"
          element={
            <PolicyGuard requiredPolicies={["property_menu"]}>
              <PropertyList />
            </PolicyGuard>
          }
        />
        <Route
          path="/property/add"
          element={
            <PolicyGuard requiredPolicies={["property_add"]}>
              <PropertyAdd />
            </PolicyGuard>
          }
        />
        <Route
          path="/property/edit/:id"
          element={
            <PolicyGuard requiredPolicies={["property_menu"]}>
              <PropertyEdit />
            </PolicyGuard>
          }
        />
        <Route
          path="/transfer/:id"
          element={
            <PolicyGuard
              requiredPolicies={[
                "transfer_property",
                "transfer_resident",
                "transfer_company",
                "merge_property",
                "split_property",
              ]}
            >
              <TransferProcessPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/transfer/:id/:companyId"
          element={
            <PolicyGuard
              requiredPolicies={[
                "transfer_property",
                "transfer_resident",
                "transfer_company",
                "merge_property",
                "split_property",
              ]}
            >
              <TransferProcessPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/product"
          element={
            <PolicyGuard requiredPolicies={["product_listing"]}>
              <ProductListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/product/add"
          element={
            <PolicyGuard requiredPolicies={["product_add"]}>
              <ProductAddPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/product/edit/:productId"
          element={
            <PolicyGuard requiredPolicies={["product_edit"]}>
              <ProductEditPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/model"
          element={
            <PolicyGuard requiredPolicies={["model_menu"]}>
              <ModelListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/model/add"
          element={
            <PolicyGuard requiredPolicies={["model_add"]}>
              <ModelAddPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/model/edit/:modelId"
          element={
            <PolicyGuard requiredPolicies={["model_edit"]}>
              <ModelEditPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/user-management"
          element={
            <AccessibleMenuGuard feature="user_management">
              <UserManagementListPage />
            </AccessibleMenuGuard>
          }
        />
        <Route
          path="/user-management/add"
          element={
            <PolicyGuard requiredPolicies={["user_management_add_user"]}>
              <UserManagementAddPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/user-management/edit/:userId"
          element={
            <PolicyGuard requiredPolicies={["user_management_edit_user"]}>
              <UserManagementEditPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/user-management/roles/:userId"
          element={
            <PolicyGuard requiredPolicies={["user_management_edit_user"]}>
              <UserManagementAssignRolePage />
            </PolicyGuard>
          }
        />
        <Route path="/application" element={<ApplicationList />} />
        <Route
          path="/application/:applicantId"
          element={<ApplicationDetailAccessGuard />}
        />
        <Route
          path="/resident"
          element={
            <PolicyGuard requiredPolicies={["resident_menu"]}>
              <ResidentListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/resident/:pagetype/:applicantId/:userId"
          element={
            <PolicyGuard requiredPolicies={["resident_detail_view"]}>
              <ResidentDetailPage />
            </PolicyGuard>
          }
        />
        <Route path="/defaults" element={<Outlet />}>
          <Route
            index
            element={
              <ReimbursementsAccessGuard requiredPolicies={["default_menu"]}>
                <ReimbursementsListPage />
              </ReimbursementsAccessGuard>
            }
          />
          <Route
            path=":pagetype/:applicantId/:userId/:reimbursementId"
            element={
              <ReimbursementsAccessGuard
                requiredPolicies={["default_detail_view"]}
              >
                <ReimbursementsDetailPage />
              </ReimbursementsAccessGuard>
            }
          />
          <Route
            path=":pagetype/:applicantId/:userId"
            element={
              <ReimbursementsAccessGuard
                requiredPolicies={["default_detail_view"]}
              >
                <ReimbursementsDetailPage />
              </ReimbursementsAccessGuard>
            }
          />
        </Route>
        <Route path="/reports" element={<Outlet />}>
          {renderReportChildRoutes()}
        </Route>
        <Route
          path="/imap-logs"
          element={
            <PolicyGuard requiredPolicies={["reports_imap_inbox"]}>
              <ImapInboxPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/imap-logs/read/:logId"
          element={
            <PolicyGuard requiredPolicies={["reports_imap_inbox"]}>
              <MailLogDetailPage variant="imap" />
            </PolicyGuard>
          }
        />
        <Route
          path="/reports-scheduler"
          element={
            <PolicyGuard requiredPolicies={["reports_scheduler_menu"]}>
              <ReportsSchedulerPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/underwriting-config"
          element={
            <AccessibleMenuGuard feature="underwriting_config">
              <UnderwritingConfigPage />
            </AccessibleMenuGuard>
          }
        />
        <Route
          path="/impact-notes"
          element={
            <PolicyGuard requiredPolicies={["impact_notes_menu"]}>
              <ImpactNotesListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/report-description"
          element={
            <AdminOnlyGuard>
              <ReportDescriptionListPage />
            </AdminOnlyGuard>
          }
        />
        <Route path="/version" element={<WhatsNewListPage />} />
        <Route
          path="/jobs"
          element={<Navigate to="/jobs/failed" replace />}
        />
        <Route
          path="/jobs/queued"
          element={
            <AdminOnlyGuard>
              <QueuedJobsPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/jobs/failed"
          element={
            <AdminOnlyGuard>
              <FailedJobsPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/jobs/cron"
          element={
            <AdminOnlyGuard>
              <CronJobsPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/jobs/cron-monitor"
          element={
            <AdminOnlyGuard>
              <CronMonitorPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/jobs/cron-monitor/list"
          element={
            <AdminOnlyGuard>
              <CronMonitorJobsListPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/jobs/cron-monitor/:kind"
          element={
            <AdminOnlyGuard>
              <CronMonitorJobsListPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/notification-settings"
          element={
            <AdminOnlyGuard>
              <NotificationSettingsListPage />
            </AdminOnlyGuard>
          }
        />
        <Route path="/policy" element={<PolicyListPage />} />
        <Route
          path="/stock"
          element={
            <PolicyGuard requiredPolicies={["stock_view_registry"]}>
              <StockRegistryListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={<DocumentManagementPage />}
        />
        <Route
          path="/terms-and-conditions/edit/:policyId"
          element={<TermsConditionsEditPage />}
        />
        <Route
          path="/gdpr-consent/gdpr-document"
          element={<DocumentManagementPage />}
        />
        <Route
          path="/gdpr-consent/edit/:documentId"
          element={<ServiceAgreementEditPage />}
        />
        <Route
          path="/gdpr-consent/view/:documentId"
          element={<ServiceAgreementViewPage />}
        />
        <Route
          path="/gdpr-consent/add"
          element={<ServiceAgreementEditPage />}
        />
        <Route path="/organizations" element={<OrganizationListPage />} />
        <Route path="/organizations/add" element={<OrganizationEditPage />} />
        <Route
          path="/organizations/edit/:orgId"
          element={<OrganizationEditPage />}
        />
        <Route
          path="/system-announcement"
          element={<SystemAnnouncementListPage />}
        />
        <Route
          path="/dashboard-tiles"
          element={
            <AdminOnlyGuard>
              <DashboardTilesListPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/mailtrap"
          element={
            <PolicyGuard requiredPolicies={["reports_mailtrap_logs"]}>
              <MailtrapListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/email-templates"
          element={
            <PolicyGuard requiredPolicies={["email_template_menu"]}>
              <EmailTemplatesListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/email-templates/add"
          element={
            <PolicyGuard requiredPolicies={["email_template_menu"]}>
              <EmailTemplateAddEditPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/email-templates/edit/:templateId"
          element={
            <PolicyGuard requiredPolicies={["email_template_menu"]}>
              <EmailTemplateAddEditPage />
            </PolicyGuard>
          }
        />
        <Route path="/user-roles" element={<RolesListPage />} />
        <Route
          path="/property-maintenance"
          element={<PropertyMaintenancePage />}
        />
        <Route path="/transfer-list" element={<PropertyMaintenanceTransferPage />} />
        <Route path="/import-data" element={<PropertyMaintenanceImportPage />} />
        <Route path="/sales-team" element={<SalesTeamsPage />} />
        <Route
          path="/config"
          element={
            <AdminOnlyGuard>
              <ConfigLegacyPage />
            </AdminOnlyGuard>
          }
        />
        <Route
          path="/screening-info-requests"
          element={
            <PolicyGuard
              requiredPolicies={["screening_info_requests_config_menu"]}
            >
              <ScreeningInfoRequestsListPage />
            </PolicyGuard>
          }
        />
        <Route
          path="/frequently-used-notes"
          element={
            <PolicyGuard
              requiredPolicies={["frequently_used_notes_config_menu"]}
            >
              <ReimbursementNotesListPage />
            </PolicyGuard>
          }
        />
      </Route>

      {/* Legacy `/dashboard/{module}` → Angular-shaped paths */}
      <Route
        path="/dashboard/residents/resident-detail/:applicantId/:userId"
        element={
          <ProtectedRoute>
            <LegacyResidentDetailRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/reimbursements/detail/:applicantId/:userId"
        element={
          <ProtectedRoute>
            <LegacyReimbursementDetailRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reimbursements/detail/:applicantId/:userId"
        element={
          <ProtectedRoute>
            <LegacyReimbursementDetailRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reimbursements/*"
        element={
          <ProtectedRoute>
            <LegacyReimbursementsPathRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reimbursements"
        element={
          <ProtectedRoute>
            <Navigate to="/defaults" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/invite/edit/:code"
        element={
          <ProtectedRoute>
            <LegacyInviteEditRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <LegacyDashboardPathRedirect />
          </ProtectedRoute>
        }
      />

      <Route
        path="/applicant"
        element={
          <ProtectedRoute>
            <ApplicantDashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<ApplicantDashboard />} />
        <Route index element={<Navigate to="/applicant/dashboard" replace />} />
        <Route path="messages" element={<Messages />} />
        <Route path="application">
          <Route
            index
            element={<Navigate to="/applicant/application/about" replace />}
          />
          <Route
            path="about"
            element={
              <ApplicationRouteGuard>
                <About />
              </ApplicationRouteGuard>
            }
          />
          <Route
            path="property"
            element={
              <ApplicationRouteGuard>
                <Property />
              </ApplicationRouteGuard>
            }
          />
          <Route
            path="income"
            element={
              <ApplicationRouteGuard>
                <Income />
              </ApplicationRouteGuard>
            }
          />
          <Route
            path="history"
            element={
              <ApplicationRouteGuard>
                <History />
              </ApplicationRouteGuard>
            }
          />
          <Route
            path="signature"
            element={
              <ApplicationRouteGuard>
                <Signature />
              </ApplicationRouteGuard>
            }
          />
          <Route
            path="payment"
            element={
              <ApplicationRouteGuard>
                <PaymentPage />
              </ApplicationRouteGuard>
            }
          />
        </Route>
        <Route path="help" element={<Help />} />
        <Route path="account" element={<Profile />} />
      </Route>

      <Route path="/" element={<RootLauncherRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/forgotpassword" element={<ForgotPassword />} />
      <Route path="/auth/forgotpassword" element={<ForgotPassword />} />
      <Route path="/resetPassword/:resetToken" element={<ResetPassword />} />
      <Route
        path="/auth/resetPassword/:resetToken"
        element={<ResetPassword />}
      />
      <Route path="/apply-now" element={<ApplyNow />} />
      <Route
        path="/apply-now/invite-code-question"
        element={<InviteCodeQuestion />}
      />
      <Route
        path="/apply-now/enter-invite-code"
        element={<EnterInviteCode />}
      />
      <Route
        path="/apply-now/verify-info"
        element={<VerifyInfoSetPassword />}
      />
      <Route
        path="/apply-now/need-to-apply-first"
        element={<NeedToApplyFirst />}
      />
      <Route
        path="/auth/signup"
        element={<Navigate to="/apply-now" replace />}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Route>,
  ),
);
